import React from 'react';

// The single source of truth for every compliance + honesty surface the
// supplement features show (SS3). Import these instead of re-writing copy, so the
// FDA disclaimer, FTC disclosure, evidence-grade chip, strength badge,
// flag-and-defer interaction banner, and food-first note all stay consistent and
// correct wherever recommendations appear (/you, /supplements, later the quiz).
//
// Colour discipline (visual system): three saturated jobs only — gold = you/
// accent (primary), green = good/strong, red = off. There is NO yellow warning
// tier; a caution is a calm neutral grey + an icon, never an alarm colour.

// --- FDA structure/function disclaimer — always shown near any rec -----------
export const FdaDisclaimer = () => (
  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
    These statements have not been evaluated by the Food and Drug Administration.
    This information is for general wellness and is not intended to diagnose, treat,
    cure, or prevent any disease. It isn’t medical advice — talk to a qualified
    clinician about your situation.
  </p>
);

// --- FTC affiliate disclosure — ONLY when a real affiliate link is present ---
export const FtcDisclosure = ({ hasAffiliate }) =>
  hasAffiliate ? (
    <p className="text-sm text-gray-500 dark:text-gray-400">
      Some links may be affiliate links; if you buy through them we may earn a
      commission at no extra cost to you. It never changes what we recommend.
    </p>
  ) : null;

const NEUTRAL_CHIP =
  'bg-gray-500/10 text-gray-600 dark:text-gray-300 border-gray-400/40';

// --- Evidence-grade chip -----------------------------------------------------
const GRADE_STYLES = {
  strong: 'bg-good/15 text-good-strong dark:text-good border-good/40',
  moderate: 'bg-primary-500/15 text-primary-700 dark:text-primary-300 border-primary-500/40',
  preliminary: NEUTRAL_CHIP,
  theoretical: NEUTRAL_CHIP,
};

export const EvidenceBadge = ({ grade }) => {
  if (!grade) return null;
  const cls = GRADE_STYLES[grade] || NEUTRAL_CHIP;
  return (
    <span className={`text-sm font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      {grade} evidence
    </span>
  );
};

// --- Recommendation-strength badge (honesty is first-class) ------------------
const STRENGTH_META = {
  recommend: { label: 'Recommended', cls: 'bg-good/15 text-good-strong dark:text-good border-good/40' },
  consider: { label: 'Worth considering', cls: 'bg-primary-500/15 text-primary-700 dark:text-primary-300 border-primary-500/40' },
  food_first: { label: 'Food first', cls: NEUTRAL_CHIP },
  skip: { label: 'Save your money', cls: NEUTRAL_CHIP },
};

export const StrengthBadge = ({ strength }) => {
  const meta = STRENGTH_META[strength] || STRENGTH_META.consider;
  return (
    <span className={`text-sm font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

// --- Flag-and-defer interaction / pregnancy banner ---------------------------
// Renders the gate's reasons (medication interaction, pregnancy caution). A calm
// neutral surface + icon — we surface the flag and step back, never dose or
// medication advice, never an alarm colour.
export const InteractionBanner = ({ reasons }) => {
  if (!reasons || reasons.length === 0) return null;
  return (
    <div className="mt-2 rounded-lg border border-surface-border dark:border-surface-dark-border bg-gray-50 dark:bg-surface-dark-raised/40 px-3 py-2">
      {reasons.map((r, i) => (
        <p key={i} className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
          ⚠️ {r}
        </p>
      ))}
    </div>
  );
};

// --- Food-first note ---------------------------------------------------------
export const FoodFirstNote = ({ note }) =>
  note ? (
    <p className="text-sm text-gray-600 dark:text-gray-300">
      🥗 {note}
    </p>
  ) : null;
